/**
 * Pure browser API interceptors
 * 
 * This module provides low-level browser API interception utilities.
 * NO BUSINESS LOGIC should be in this file - it's pure infrastructure.
 * 
 * Business logic (URL transformations, flags, etc.) should be injected
 * via callbacks from the services layer.
 */

const nativeOpenKey = '_nativeOpen';

export type UrlTransformer = (url: string) => string;
export type TargetTransformer = (target?: string) => string | undefined;

/**
 * Creates a window.open() interceptor with custom URL transformation
 * 
 * @param urlTransformer - Function to transform URLs before opening
 * @param targetTransformer - Optional function to transform the target parameter
 * 
 * @example
 * ```typescript
 * createWindowOpenInterceptor(transformUrl, transformTarget);
 * ```
 */
export const createWindowOpenInterceptor = (
    urlTransformer: UrlTransformer,
    targetTransformer?: TargetTransformer,
): void => {
    const nativeOpen = window.open;

    const customOpen: typeof window.open = (
        url?: string | URL,
        target?: string,
        features?: string,
    ) => {
        const transformedUrl = url ? urlTransformer(String(url)) : url;
        const transformedTarget = targetTransformer ? targetTransformer(target) : target;
        return nativeOpen(transformedUrl, transformedTarget, features);
    };

    window.open = customOpen;
    (window as any)[nativeOpenKey] = nativeOpen;
};

/**
 * Restores the native window.open() implementation
 */
export const restoreWindowOpen = (): void => {
    const nativeOpen = (window as any)[nativeOpenKey];
    if (nativeOpen) {
        window.open = nativeOpen;
        delete (window as any)[nativeOpenKey];
    }
};

